/**
 * Utility functions for the application
 */

/**
 * Calculate income gap percentage
 */
export const calculateIncomeGap = (userIncome: number, spouseIncome: number): number => {
  if (!userIncome || !spouseIncome || userIncome === 0) return 0;

  const higherIncome = Math.max(userIncome, spouseIncome);
  const gap = Math.abs(userIncome - spouseIncome);
  return (gap / higherIncome) * 100;
};

/**
 * Format currency
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Generate unique session ID
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Validate email
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate date
 */
export const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Calculate total assets
 */
export const calculateTotalAssets = (assets?: any): number => {
  if (!assets) return 0;

  let total = 0;

  if (assets.realEstate) {
    total += assets.realEstate.reduce((sum: number, prop: any) => sum + (prop.value || 0), 0);
  }

  if (assets.vehicles) {
    total += assets.vehicles.reduce((sum: number, v: any) => sum + (v.value || 0), 0);
  }

  if (assets.cash) {
    total += assets.cash.reduce((sum: number, c: any) => sum + (c.amount || 0), 0);
  }

  if (assets.investments) {
    total += assets.investments.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
  }

  if (assets.retirement) {
    total += assets.retirement.reduce((sum: number, r: any) => sum + (r.amount || 0), 0);
  }

  if (assets.other) {
    total += assets.other.reduce((sum: number, o: any) => sum + (o.amount || 0), 0);
  }

  return total;
};

/**
 * Calculate total liabilities
 */
export const calculateTotalLiabilities = (liabilities?: any): number => {
  if (!liabilities) return 0;

  let total = 0;

  if (liabilities.creditCards) {
    total += liabilities.creditCards.reduce((sum: number, card: any) => sum + (card.amount || 0), 0);
  }

  if (liabilities.personalLoans) {
    total += liabilities.personalLoans.reduce((sum: number, loan: any) => sum + (loan.amount || 0), 0);
  }

  if (liabilities.other) {
    total += liabilities.other.reduce((sum: number, debt: any) => sum + (debt.amount || 0), 0);
  }

  return total;
};

/**
 * Calculate net assets
 */
export const calculateNetAssets = (assets?: any, liabilities?: any): number => {
  return calculateTotalAssets(assets) - calculateTotalLiabilities(liabilities);
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};